import { getHint, SudokuStrategy } from "sudokuru";
import {
  CellProps,
  ClassicObjectProps,
  PersistedHintPayload,
} from "../../../../Functions/LocalDatabase";
import { generateBoxIndex } from "./CellFunctions";

/**
 * Retrieves a hint for the current sudoku puzzle state based on the specified strategy order.
 * The hint is generated using the current puzzle state, solution, and an strategy array that determines priority order of hint.
 * Hint information is then processed through the hintInjections function to further customize the output.
 * @param puzzle - current sudoku puzzle state
 * @param solution - sudoku solution
 * @param strategies - order of strategies to use for generating the hint
 * @returns Hint object containing the hint information
 */
export const getSudokuHint = (
  puzzle: CellProps[][],
  strategies: SudokuStrategy[],
  solution?: number[][],
): PersistedHintPayload => {
  const puzzleState = convertPuzzleStateToSudokuruFormat(puzzle);

  let hint: PersistedHintPayload;
  if (solution) {
    const puzzleSolution = convertPuzzleSolutionToSudokuruFormat(solution);
    hint = getHint(
      puzzleState.puzzleValues,
      puzzleState.puzzleNotes,
      strategies,
      puzzleSolution,
    ) as unknown as PersistedHintPayload;
  } else {
    hint = getHint(
      puzzleState.puzzleValues,
      puzzleState.puzzleNotes,
      strategies,
    ) as unknown as PersistedHintPayload;
  }
  hint = hintInjections(hint);
  return { ...hint, simplifyNotesAfterPlacement: false };
};

/**
 * This function takes in the hint object and overrides / injects values
 * The Frontend uses groups to indicate regions of interest of a hint, whereas sudokuru package uses groups
 * to store what groups cause a strategy to occur. OBVIOUS_SINGLE is not caused by any groups, but Frontend wants to highlight the box
 * @param hint Hint object returned from sudokuru package
 * @returns Updated Hint object
 */
export const hintInjections = (hint: PersistedHintPayload) => {
  if (hint.strategy === "OBVIOUS_SINGLE") {
    hint.groups.push([2, generateBoxIndex(hint.cause[0][0], hint.cause[0][1])]);
  }
  return hint;
};

/**
 * Conversion function to be used for retrieving hints from Sudokuru package.
 * Sudokuru package uses string[][] for solution, while Frontend uses number[][]
 * @param puzzleSolution Sudoku puzzle solution in Frontend format
 * @returns Sudoku puzzle solution in Sudokuru package format
 */
const convertPuzzleSolutionToSudokuruFormat = (
  puzzleSolution: number[][],
): string[][] => {
  const convertedPuzzleSolution: string[][] = [];
  puzzleSolution.forEach(function (value, index) {
    convertedPuzzleSolution.push([]);
    convertedPuzzleSolution[index] = value.map(String);
  });
  return convertedPuzzleSolution;
};

/**
 * Conversion function to be used for retrieving hints from Sudokuru package.
 * Sudokuru package uses two string[][] objects for representing notes and values.
 * @param puzzle Sudoku puzzle notes and values in Frontend format
 * @returns
 */
const convertPuzzleStateToSudokuruFormat = (puzzle: CellProps[][]) => {
  const convertedPuzzleValues: string[][] = [];
  const convertedPuzzleNotes: string[][] = [];
  for (let r = 0; r < puzzle.length; r++) {
    convertedPuzzleValues.push([]);
    for (let c = 0; c < puzzle[r].length; c++) {
      convertedPuzzleNotes.push([]);
      let value: string = "0";
      if (puzzle[r][c].type === "note") {
        const entry = puzzle[r][c].entry as number[];
        for (const note of entry) {
          convertedPuzzleNotes[c + r * puzzle.length].push(note.toString());
        }
      } else {
        const entry = puzzle[r][c].entry as number;
        value = entry.toString();
      }

      convertedPuzzleValues[r].push(value);
    }
  }
  return {
    puzzleValues: convertedPuzzleValues,
    puzzleNotes: convertedPuzzleNotes,
  };
};

/**
 * Retrieves a classic hint after prioritizing note-maintenance strategies.
 * Returns new board and statistics objects with updated hint counts without mutating the inputs.
 * @param sudokuBoard - current sudoku board state including puzzle state and statistics
 * @param strategyArray - base strategy order after the note-maintenance strategies
 * @returns The hint and a new board containing the incremented statistics
 */
export const getSudokuBoardHint = (
  sudokuBoard: ClassicObjectProps,
  strategyArray: SudokuStrategy[],
) => {
  const hint = getSudokuHint(
    sudokuBoard.puzzleState,
    ["AMEND_NOTES", "SIMPLIFY_NOTES", ...strategyArray],
    sudokuBoard.puzzleSolution,
  );

  const strategyIndex =
    sudokuBoard.statistics.numHintsUsedPerStrategy.findIndex(
      ({ hintStrategy }) => hintStrategy === hint.strategy,
    );
  let numHintsUsedPerStrategy;
  if (strategyIndex === -1) {
    numHintsUsedPerStrategy = [
      ...sudokuBoard.statistics.numHintsUsedPerStrategy,
      {
        hintStrategy: hint.strategy,
        numHintsUsed: 1,
      },
    ];
  } else {
    numHintsUsedPerStrategy =
      sudokuBoard.statistics.numHintsUsedPerStrategy.map((strategy, index) => {
        if (index === strategyIndex) {
          return { ...strategy, numHintsUsed: strategy.numHintsUsed + 1 };
        }
        return strategy;
      });
  }

  return {
    hint,
    updatedBoard: {
      ...sudokuBoard,
      statistics: {
        ...sudokuBoard.statistics,
        numHintsUsed: sudokuBoard.statistics.numHintsUsed + 1,
        numHintsUsedPerStrategy,
      },
    },
  };
};
